import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Pencil, Trash2, Search, Music, FileAudio, Loader2 } from 'lucide-react';
import { Alert, AlertDescription } from '../ui/alert';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/table';
import { Musikk, MusikkKategori, musikkAPI } from '../../services/api';
import ConfirmDialog from '../common/ConfirmDialog';

const MusikkbibliotekList: React.FC = () => {
  const navigate = useNavigate();
  const [musikk, setMusikk] = useState<Musikk[]>([]);
  const [, setKategorier] = useState<MusikkKategori[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [musikkToDelete, setMusikkToDelete] = useState<Musikk | null>(null);

  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);
      const [musikkData, kategoriData] = await Promise.all([
        musikkAPI.getAll(),
        musikkAPI.getAllKategorier(),
      ]);
      setMusikk(musikkData.musikk);
      setKategorier(kategoriData.kategorier);
    } catch (err: any) {
      setError('Kunne ikke laste sangbibliotek');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenCreate = () => {
    navigate('/musikk/ny');
  };

  const handleOpenEdit = (m: Musikk) => {
    navigate(`/musikk/${m.id}`);
  };

  const handleDeleteClick = (m: Musikk) => {
    setMusikkToDelete(m);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!musikkToDelete) return;

    try {
      await musikkAPI.delete(musikkToDelete.id);
      setSuccess(`"${musikkToDelete.tittel}" slettet!`);
      setTimeout(() => setSuccess(null), 3000);
      setDeleteDialogOpen(false);
      setMusikkToDelete(null);
      fetchData();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Kunne ikke slette sang');
      setDeleteDialogOpen(false);
    }
  };

  const filteredMusikk = musikk.filter((m) => {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    return (
      m.tittel.toLowerCase().includes(term) ||
      m.artist?.toLowerCase().includes(term) ||
      m.album?.toLowerCase().includes(term)
    );
  });

  if (loading) {
    return (
      <div className="flex justify-center p-8">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div>
      {error && (
        <Alert variant="destructive" className="mb-4">
          <AlertDescription className="flex items-center justify-between gap-2">
            <span>{error}</span>
            <button onClick={() => setError(null)} className="text-sm underline">
              Lukk
            </button>
          </AlertDescription>
        </Alert>
      )}

      {success && (
        <Alert className="mb-4 border-status-confirmed/40 bg-status-confirmed/10">
          <AlertDescription className="flex items-center justify-between gap-2">
            <span>{success}</span>
            <button onClick={() => setSuccess(null)} className="text-sm underline">
              Lukk
            </button>
          </AlertDescription>
        </Alert>
      )}

      <div className="flex justify-between items-center mb-6 gap-3 flex-wrap">
        <div className="relative w-full max-w-[400px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Søk etter tittel, artist eller album..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-9"
          />
        </div>

        <Button onClick={handleOpenCreate}>
          <Plus className="mr-2 h-4 w-4" />
          Ny sang
        </Button>
      </div>

      {filteredMusikk.length === 0 ? (
        <div className="rounded-lg border bg-muted p-8 text-center">
          <Music className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
          <Alert>
            <AlertDescription>
              {searchTerm
                ? `Ingen sanger funnet for "${searchTerm}"`
                : 'Ingen sanger lagt til ennå. Klikk "Ny sang" for å komme i gang.'}
            </AlertDescription>
          </Alert>
        </div>
      ) : (
        <div className="rounded-md border overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-muted">
                <TableHead className="font-semibold">Tittel</TableHead>
                <TableHead className="font-semibold">Artist</TableHead>
                <TableHead className="font-semibold">Album</TableHead>
                <TableHead className="font-semibold">År</TableHead>
                <TableHead className="font-semibold">Kategorier</TableHead>
                <TableHead className="font-semibold">Filer</TableHead>
                <TableHead className="font-semibold">Status</TableHead>
                <TableHead className="font-semibold text-right">Handlinger</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredMusikk.map((m) => (
                <TableRow key={m.id}>
                  <TableCell className="font-medium">{m.tittel}</TableCell>
                  <TableCell>{m.artist || '-'}</TableCell>
                  <TableCell>{m.album || '-'}</TableCell>
                  <TableCell>{m.utgivelsesaar || '-'}</TableCell>
                  <TableCell>
                    <div className="flex gap-1 flex-wrap">
                      {m.kategorier.length > 0 ? (
                        m.kategorier.map((kat) => (
                          <Badge key={kat.id} variant="outline">
                            {kat.navn}
                          </Badge>
                        ))
                      ) : (
                        <span className="text-muted-foreground">Ingen</span>
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="text-center">
                    {m.fil_count && m.fil_count > 0 ? (
                      <div className="flex items-center justify-center gap-1">
                        <FileAudio className="h-5 w-5 text-primary" />
                        <span className="text-sm font-medium">{m.fil_count}</span>
                      </div>
                    ) : (
                      <span className="text-muted-foreground text-sm">-</span>
                    )}
                  </TableCell>
                  <TableCell>
                    <Badge
                      className={
                        m.publisert
                          ? 'bg-status-confirmed text-white hover:bg-status-confirmed'
                          : 'bg-muted text-foreground hover:bg-muted'
                      }
                    >
                      {m.publisert ? 'Publisert' : 'Ikke publisert'}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right space-x-1">
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8"
                      onClick={() => handleOpenEdit(m)}
                      title="Rediger"
                    >
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 text-destructive hover:text-destructive"
                      onClick={() => handleDeleteClick(m)}
                      title="Slett"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      <ConfirmDialog
        open={deleteDialogOpen}
        title="Slett sang"
        message={`Er du sikker på at du vil slette "${musikkToDelete?.tittel}"? Dette kan ikke angres.`}
        confirmText="Slett"
        cancelText="Avbryt"
        destructive
        onCancel={() => {
          setDeleteDialogOpen(false);
          setMusikkToDelete(null);
        }}
        onConfirm={handleDeleteConfirm}
      />
    </div>
  );
};

export default MusikkbibliotekList;
